import React, { useEffect, useState, useRef, useContext } from 'react';
import {useRouteMatch} from 'react-router-dom';
import classnames from 'classnames';
import ReactSVG from 'react-svg';
import filterCartProductsByRecs from "../../utils/filterCartProductsByRecs.mjs";
import {sendFormToHubspot, sendLeadToSalesforce} from "../../actions/integration-action-creators.mjs";
import ShareViaEmailModal from "../ShareViaEmailModal";
import Product from "../Product";
import CompleteGuideButton from "../CompleteGuideButton";
import CompleteGuideFlow from "../CompleteGuideFlow";
import {processExistingProducts} from "../../actions/integration-action-creators";
import {useDispatch, useGuideConfig, useProducts, triggerIgniteAction, usePages, useNeuroState, AnswersList, contactLeadUpdate, NeuroMessage, clearNotifications, notifyError, notifyInfo, notifySuccess, sendResultsViaEmail} from "@ignitesales/neuro-application";
import getGuideId from "../../utils/getBranchId.mjs";

function SummaryPage({ componentClass, productsList: productsRecommendedList }) {
  const { salesforceError, salesforceIsProcessing, salesforceLeadTransmitted, answers, user } = useNeuroState();
  const { productCart, products } = useProducts();
  const guideContext = useGuideConfig();
  const dispatch = useDispatch();
  const { goToPreviousPage } = usePages();
  const runMode = guideContext.runMode;
  const matchDetailsPage = useRouteMatch('/guide/:uuid/details');
  const [ sticky, setSticky ] = useState(false);
  const [ showForm, setShowForm ] = useState(false);
  const [ showEmailModal, setEmailModalView ] = useState(false);
  const prevProcessingSF = useRef();
  const answersSection = useRef(null);
  const pageCssBEM = 'summaryPage';
  const classes = classnames( pageCssBEM, componentClass);
  const [ leadTransmitted, setLeadTransmitted ] = useState(Object.hasOwnProperty.call(answers, 'leadTransmitted'));
  const [ productRedirectUrl, setProductRedirectUrl ] = useState('');
  const [ clickedProduct, setClickedProduct ] = useState();
  const [ leadInfo, setLeadInfo ] = useState();

  const actionCompleted = (actionName, productId, value, append) => {
    dispatch(triggerIgniteAction({actionName, productId, value, append}));
  };

  useEffect(() => { dispatch(processExistingProducts()) }, []);

  useEffect(() => {
    stickyElementScrollHandler();
    window.addEventListener('scroll', stickyElementScrollHandler);
    return () => {
      window.removeEventListener('scroll', stickyElementScrollHandler);
    };
  }, []);

  useEffect(() => {
    if (salesforceError) {
      notifyError('salesforceSubmission.submitted', 'top-center');
    } else if (salesforceIsProcessing && !salesforceError) {
      notifyInfo('salesforceSubmission.submitted', 'top-center');
    } else if (prevProcessingSF.current && !salesforceIsProcessing && salesforceLeadTransmitted?.value && !salesforceError) {
      notifySuccess('salesforceSubmission.submitted', 'top-center');
    } else if (!salesforceError && !salesforceIsProcessing && !salesforceLeadTransmitted?.value) {
      clearNotifications();
    }

    prevProcessingSF.current = salesforceIsProcessing;
  }, [salesforceIsProcessing, salesforceError, salesforceLeadTransmitted]);

  const stickyElementScrollHandler = () => {
    const threshold = 300;
    const position = document.getElementsByTagName("html")[0].scrollTop;
    setSticky(position >= threshold);
  };

  const existingProducts = answers.existingCustomerProducts && answers.existingCustomerProducts.value ? answers.existingCustomerProducts.value : [];

  const useApplyProducts = new Map([
    // productid, productcode
    [16100,'IMD125'],  // Freestyle Checking
    [16101,'IMD415'],  // Connect Checking
    [16102,'IMD260'],  // Priority Checking
    [16108,'IMD550'],  // Money Market Account ?
    [16159,'STM002'],  // Freestyle Savings
    [16119,''],        // Visa Platinum Credit Card
    [16157,'IMD130'],  // Assure Checking
    [16114, ''], // Personal Unsecured Loan
    [16116, ''], // Home Equity Line of Credit
  ]);
  const usePlainCtaProducts = new Map([
    // productid, productcode
    [16104,''],  // Certificate of Deposit CD
    [16119,''],  // Visa Platinum Credit Card
    [16114, ''], // Personal Unsecured Loan
    [16116, ''], // Home Equity Line of Credit
  ]);
  const applyProducts = productsRecommendedList.filter(p => useApplyProducts.has(p.id));
  const contactProducts = productsRecommendedList.filter(p => !useApplyProducts.has(p.id));

  const renderProduct = product => {
    const isInTheCart = (productCart || new Set([])).has(product.id.toString());
    const onApplyClick = url => {
      actionCompleted('productApplyClicked', product.id);
      setClickedProduct(product);

      if(!leadTransmitted) {
        setProductRedirectUrl(url);
        setShowForm(true);
      } else {
        url = appendApplyParams(url, product, leadInfo);
        url && window.open( url, '_blank');
      }
    }
    return <Product
      key={ product.id }
      isSelected={isInTheCart}
      onApplyClick={ onApplyClick }
      product={product}
      productActionCompleted={actionCompleted}
      existingProducts={existingProducts}
      user={user}
      showApplyButton={useApplyProducts.has(product.id)}
    />;
  };

  const salesforceSubmission = () => {
    actionCompleted('getStartedClicked');
    dispatch(sendLeadToSalesforce());
  };

  const onEmailComplete = () => {
    actionCompleted('sendEmailClicked');
  };

  const onOnlineFormSubmission = lead => {
    const url = clickedProduct ? appendApplyParams(productRedirectUrl, clickedProduct, lead) : false;
    setLeadTransmitted(true);
    setLeadInfo(lead);
    //dispatch(sendResultsViaEmail({ fromEmail: 'no-reply@hancockwhitney.com', email: lead.email }));
    dispatch(sendFormToHubspot({guideId: getGuideId({ isBranchGuide: guideContext.isBranchGuide, guideType: guideContext.guideType }), lead}))
    dispatch(contactLeadUpdate({contactLead: lead}))
    setProductRedirectUrl(url);

    if(url) {
      window.open(url, '_blank');
      setProductRedirectUrl('');
    }
  };

  const appendApplyParams = (url, product, lead) => {
    console.log('url: ', url, ' product: ', product, ' lead: ', lead);
    const newUrl = new URL(url);
    // if in apply products and runmode matches or override url param found
    const productMatch = useApplyProducts.has(product.id) && !usePlainCtaProducts.has(product.id);
    const isProd = (new URL(document.location)).searchParams.get('prodParamTest') === 'true' || runMode.toLowerCase() === "production";
    const isPilot = (new URL(document.location)).searchParams.get('paramTest') === 'true' || runMode.toLowerCase() === "pilot" || runMode.toLowerCase() === "staging";
    const runModeMatch = isProd || isPilot ;
    const hostNameByRunMode = isProd ? 'apply.hancockwhitney.com' : 'apply-sit-teller.hancockwhitney.com';

    console.log("productMatch? ", productMatch, " runModeMatch? ", runModeMatch);

    if( productMatch && runModeMatch && guideContext.isBranchGuide === false) {
      // parameter order matters
      // newUrl.searchParams.clear();
      newUrl.hostname = hostNameByRunMode;
      newUrl.searchParams.set('fiIdent', 'HWB');
      newUrl.searchParams.set('productCode', useApplyProducts.get(product.id));
      newUrl.searchParams.set('zipcode', answers.hasOwnProperty('postalCode') ? answers.postalCode.value[0] : '');
      if(lead){
        newUrl.searchParams.set('email', lead.email);
        newUrl.searchParams.set('igniteUUID', user.uuid);
        newUrl.searchParams.set('firstName', lead.firstName);
        newUrl.searchParams.set('lastName', lead.lastName);
      }else{
        newUrl.searchParams.set('igniteUUID', user.uuid);
      }
      newUrl.hash = '#!/acctopening';
    }
    return newUrl;
  }

  const onOnlineEmailClick = () => {
    setShowForm(true);
  };

  return (
    <div className={classes}>

      <div className={ `${ pageCssBEM }__container` }>

        { !guideContext.isBranchGuide && <div className={`${ pageCssBEM }__header`}>
          <NeuroMessage id='summary.steps.title' tagName='h1'/>
          <NeuroMessage id='summary.steps.subtitle' tagName='p'/>
        </div> }

        <div className={ `${ pageCssBEM }__header` }>
          <NeuroMessage id='summary.recommendations.title' tagName={ guideContext.isBranchGuide ? 'h1' : 'h2'}/>
          <NeuroMessage id='summary.recommendations.subtitle' tagName='p'/>
        </div>

        <div className={ `${ pageCssBEM }__actions`}>
          <div className={`${ pageCssBEM }__actions-wrapper`}>
            <div className={ guideContext.isBranchGuide ? 'fc-action-bar' : 'online-action-bar' }>
              {  guideContext.isBranchGuide && <NeuroMessage id='summary.actions.title' tagName='h2'/> }
              {  guideContext.isBranchGuide &&
              <button
                className="btn action btn--email"
                onClick={() => {
                  guideContext.isBranchGuide && actionCompleted('shareViaEmailClicked');
                  guideContext.isBranchGuide ? setEmailModalView(true) : setShowForm(true);
                }}
                role="button"
                aria-label="email action"
              ><NeuroMessage id='summary.actions.email' tagName="span"/></button>
              }
              { guideContext.isBranchGuide && <CompleteGuideButton
                isProcessing={salesforceIsProcessing}
                onClick={ () => salesforceSubmission() }
              /> }
              { salesforceIsProcessing && <span className='loading-icon--salesforce'> <ReactSVG src="/classpath/assets/images/loading.svg" wrapper='span'/></span> }
              <CompleteGuideFlow
                formIsVisible={showForm}
                onCloseForm={() => setShowForm(false)}
                onFormSubmission={onOnlineFormSubmission}
                hasTransmittedLead={leadTransmitted}
              />
            </div>

            <div className='action-bar'>
              <button className="btn action action--print" onClick={() => { window.print(); actionCompleted('printButtonClicked') }} role="button" aria-label="Print">
                <NeuroMessage id='summary.actions.print' tagName="span"/>
              </button>
              { !guideContext.isBranchGuide &&
              <button className="btn action action--email" onClick={onOnlineEmailClick} role="button" aria-label="Email conversation details">
                <NeuroMessage id='summary.actions.email' tagName="span"/>
              </button> }
              <button className="btn action action--answersList" onClick={() => document.getElementById("answersSection").scrollIntoView()} role="button" aria-label="View Answers">
                <NeuroMessage id='summary.actions.answers' tagName="span"/>
              </button>
            </div>
          </div>
        </div>
        <div className={ `${ pageCssBEM }__products` }>
        { // renderApplyProducts
          applyProducts.map(p => renderProduct(p))
        }
        </div>
        {  !guideContext.isBranchGuide &&
        <div className={ `${ pageCssBEM }__actions contact-message`}>
          <CompleteGuideButton
            isProcessing={salesforceIsProcessing}
            onClick={ () => setShowForm(true) }
          />
          <NeuroMessage shouldHideIfNotFound={true} id='summary.steps.contact' tagName="span"/>
        </div>
        }

        <div className={ `${ pageCssBEM }__products` }>
        { // renderContactProducts
          contactProducts.map(p => renderProduct(p))
        }
        </div>
        {matchDetailsPage ? null :
          <button onClick={goToPreviousPage} className="btn__back" role="button" aria-label="Go Back">
            <i className="fa fa-caret-left" aria-hidden="true"/>
            <NeuroMessage id="summary.back" tagName="span"/>
          </button>
        }
      </div>

      <div ref={answersSection} className={ `${ pageCssBEM }__answers`} id="answersSection">
        <div className={ `${ pageCssBEM }__container` }>
          <NeuroMessage id="summary.yourAnswers" tagName="h2" />
          <AnswersList answers={ answers } actionCompleted={ actionCompleted } />
        </div>
      </div>

      <ShareViaEmailModal
        products={productsRecommendedList}
        active={ showEmailModal }
        onEmailComplete={onEmailComplete}
        onClose={ () => setEmailModalView(false) }
      />
    </div>
  );
}

export default SummaryPage;
